// src/services/api.js
import axios from 'axios';
const getBaseURL = () => {
  if (import.meta.env.PROD) {
    // Use your actual backend URL
    return 'https://securebahrain.com/api';
  } else {
    // In development, use proxy (points to your backend)
    return '/api';
  }
};
const API_BASE_URL =  getBaseURL(); // Using proxy

console.log('🔗 API Base URL:', API_BASE_URL);

// Enhanced timeout configuration
const TIMEOUT_CONFIG = {
  default: 15000, // 15 seconds default
  auth: 30000,    // 30 seconds for auth operations
  upload: 60000,  // 60 seconds for file uploads
  download: 30000 // 30 seconds for downloads
};

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: TIMEOUT_CONFIG.default,
});

// Enhanced request interceptor with timeout management
api.interceptors.request.use(
  (config) => {
    console.log(`🚀 Making ${config.method?.toUpperCase()} request to:`, config.url);
    
    // Set appropriate timeout based on request type
    if (config.url.includes('/Auth/')) {
      config.timeout = TIMEOUT_CONFIG.auth;
    } else if (config.headers['Content-Type']?.includes('multipart/form-data')) {
      config.timeout = TIMEOUT_CONFIG.upload;
    } else if (config.responseType === 'blob') {
      config.timeout = TIMEOUT_CONFIG.download;
    }

    const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    console.error('❌ Request error:', error);
    return Promise.reject(error);
  }
);

// Enhanced response interceptor with better error handling
api.interceptors.response.use(
  (response) => {
    console.log(`✅ ${response.status} Response from:`, response.config.url);
    console.log('📦 Response data:', response.data);
    return response;
  },
  (error) => {
    const url = error.config?.url;
    const method = error.config?.method?.toUpperCase();

    // Export/download requests use responseType: 'blob'. When the server
    // returns an error status (e.g. 500) for one of those, axios still hands
    // back the error body as a Blob instead of parsed JSON -- so
    // error.response.data was an opaque Blob object and error.message was
    // just axios's generic "Request failed with status code 500", with the
    // real backend message (e.g. "Export failed: ...") thrown away. Decode
    // it back to text/JSON here so callers (and the UI) see the actual cause.
    //
    // This used to only fire when the blob's content-type contained "json",
    // but ASP.NET Core's StatusCode(500, "some string") response -- which is
    // exactly what the report export endpoints return on failure -- serializes
    // as "text/plain", not JSON. That mismatch meant every export error body
    // was silently dropped and the UI only ever saw the generic axios message.
    // Any error-response Blob is a small text/JSON error payload (a real file
    // download only ever comes back on the success path above), so decode
    // unconditionally rather than gating on content-type.
    if (error.response?.data instanceof Blob) {
      return error.response.data.text().then((text) => {
        let message = error.message;
        try {
          const parsed = JSON.parse(text);
          error.response.data = parsed;
          message = parsed.message || parsed.title || message;
        } catch {
          error.response.data = text;
          message = text || message;
        }
        error.message = message;
        console.error('❌ API Error (decoded from blob):', { url, method, status: error.response?.status, message });
        return Promise.reject(error);
      }).catch(() => Promise.reject(error));
    }

    if (error.code === 'ECONNABORTED') {
      console.error('⏰ Request timeout:', {
        url,
        method,
        timeout: error.config.timeout,
        message: 'Request timed out. Please try again.'
      });
      
      return Promise.reject({
        ...error,
        message: `Request timeout after ${error.config.timeout}ms. Please check your connection and try again.`,
        isTimeout: true
      });
    }

    console.error('❌ API Error:', {
      url,
      method,
      status: error.response?.status,
      statusText: error.response?.statusText,
      data: error.response?.data,
      message: error.message
    });

    if (error.response?.status === 401) {
      const originalRequest = error.config;
      const hasStoredSession =
        !!(localStorage.getItem('authToken') || sessionStorage.getItem('authToken'));

      // Right after the FIRST login against a freshly-started ("npm run dev"
      // + a just-launched backend) app, the Dashboard fires a dozen-plus
      // requests at once (its own charts + the Feedback Overview widgets).
      // On a cold backend, one of those can come back 401 even though the
      // token is perfectly valid -- the JWT bearer handler's first-ever
      // validation appears to race its own signing-key/user lookup warm-up,
      // and the very next request with the SAME token succeeds. Without
      // this, that single flaky 401 nuked the whole session and bounced the
      // user straight back to /login seconds after a correct login, even
      // though every other request on the page had just succeeded. A second
      // login "just worked" only because the backend was warm by then.
      //
      // So: retry the one failing request ONCE (same token, no changes)
      // before treating it as a real "you are logged out". A genuinely
      // invalid/expired token will still 401 again on the retry and fall
      // through to the real logout below -- this only rescues the transient
      // cold-start case.
      if (hasStoredSession && originalRequest && !originalRequest._retried401) {
        originalRequest._retried401 = true;
        console.warn('🔁 401 on first attempt, retrying once before treating the session as invalid:', url);
        return new Promise((resolve) => setTimeout(resolve, 400)).then(() => api(originalRequest));
      }

      console.log('🔒 Unauthorized access, clearing credentials...');
      localStorage.removeItem('authToken');
      localStorage.removeItem('userData');
      sessionStorage.removeItem('authToken');
      sessionStorage.removeItem('userData');

      // Only redirect if not already on login page
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login';
      }
    }

    return Promise.reject(error);
  }
);

// Auth API with enhanced timeout
export const authAPI = {
  login: (email, password) => api.post('/Auth/login', { email, password }, { 
    timeout: TIMEOUT_CONFIG.auth 
  }),
  register: (userData) => api.post('/Auth/register', userData, { 
    timeout: TIMEOUT_CONFIG.auth 
  }),
  getCurrentUser: () => api.get('/Auth/me'),
  getUsers: () => api.get('/Auth/users'),
  forgotPassword: (email) => api.post('/Auth/forgot-password', { email }, { 
    timeout: TIMEOUT_CONFIG.auth 
  }),
  resetPassword: (token, newPassword) => api.post('/Auth/reset-password', { token, newPassword }, { 
    timeout: TIMEOUT_CONFIG.auth 
  }),
};
export const usersAPI = {
  updateProfile: (profileData) => api.put('/Profile', profileData),
  getProfile: () => api.get('/Profile'),
};
// Companies API
export const companiesAPI = {
  getAll: () => api.get('/Companies'),
  getById: (id) => api.get(`/Companies/${id}`),
  create: (company) => api.post('/Companies', company),
  update: (id, company) => api.put(`/Companies/${id}`, company),
};
export const branchesAPI = {
  getAll: () => api.get('/Branches'),
  getById: (id) => api.get(`/Branches/${id}`),
  create: (branch) => api.post('/Branches', branch),
  update: (id, branch) => api.put(`/Branches/${id}`, branch),
  delete: (id) => api.delete(`/Branches/${id}`),
};

export const departmentsAPI = {
  getAll: () => api.get('/Departments'),
  getById: (id) => api.get(`/Departments/${id}`),
  create: (department) => api.post('/Departments', department),
  update: (id, department) => api.put(`/Departments/${id}`, department),
  delete: (id) => api.delete(`/Departments/${id}`),
};

export const divisionsAPI = {
  getAll: () => api.get('/Divisions'),
  getById: (id) => api.get(`/Divisions/${id}`),
  create: (division) => api.post('/Divisions', division),
  update: (id, division) => api.put(`/Divisions/${id}`, division),
  delete: (id) => api.delete(`/Divisions/${id}`),
};

// Employees API
export const employeesAPI = {
  getAll: () => api.get('/Employees'),
  getById: (id) => api.get(`/Employees/${id}`),
  create: (employee) => api.post('/Employees', employee),
  update: (id, employee) => api.put(`/Employees/${id}`, employee),
  delete: (id) => api.delete(`/Employees/${id}`),
  get: (url, config) => api.get(`/Employees${url}`, config),
  post: (url, data, config) => api.post(`/Employees${url}`, data, config),
};


// Surveys API
export const surveysAPI = {
  getAll: () => api.get('/Surveys'),
  getById: (id) => api.get(`/Surveys/${id}`),
  create: (survey) => api.post('/Surveys', survey),
  update: (id, survey) => api.put(`/Surveys/${id}`, survey),
  delete: (id) => api.delete(`/Surveys/${id}`),
  getCategories: (surveyId) => api.get(`/Surveys/${surveyId}/categories`),
  getResponses: (surveyId) => api.get(`/Surveys/${surveyId}/responses`),
  reorderCategories: (surveyId, categoryOrders) => api.post(`/Surveys/${surveyId}/categories/reorder`, categoryOrders),
  getPublicSurvey: (surveyId, config) => {
    return api.get(`/Surveys/public/${surveyId}`, config);
  },
  
  submitPublicSurveyResponses: (surveyId, data) => {
    return api.post(`/Surveys/public/${surveyId}/responses`, data);
  },
};

export const surveyCategoriesAPI = {
  getBySurvey: (surveyId) => api.get(`/SurveyCategories/survey/${surveyId}`),
  getById: (id) => api.get(`/SurveyCategories/${id}`),
  create: (category) => api.post('/SurveyCategories', category),
  update: (id, category) => api.put(`/SurveyCategories/${id}`, category),
  delete: (id) => api.delete(`/SurveyCategories/${id}`),
  reorder: (categoryOrders) => api.post('/SurveyCategories/reorder', categoryOrders),
};
export const questionsAPI = {
  getByCategory: (categoryId) => api.get(`/SurveyQuestions/category/${categoryId}`),
  getBySurvey: (surveyId) => api.get(`/SurveyQuestions/survey/${surveyId}`), 
  getSurveyWithCategories: (surveyId) => api.get(`/SurveyQuestions/survey/${surveyId}`),
  getById: (id) => api.get(`/SurveyQuestions/${id}`),
  create: (question) => api.post('/SurveyQuestions', question),
  update: (id, question) => api.put(`/SurveyQuestions/${id}`, question),
  delete: (id) => api.delete(`/SurveyQuestions/${id}`),
  reorder: (questionOrders) => api.post('/SurveyQuestions/reorder', questionOrders),
};
// Survey Link API
export const surveyLinksAPI = {
  getSurveyLinks: (surveyId) => api.get(`/SurveyLinks/survey/${surveyId}`),
  rregeneratePrivateLink: (surveyId) => api.post('/SurveyLinks/regenerate-private', { 
    SurveyId: surveyId, 
    LinkType: 'Private Link' 
  }),
  regeneratePublicLink: (surveyId) => api.post('/SurveyLinks/regenerate-public', { 
    SurveyId: surveyId, 
    LinkType: 'Public Link' 
  }),
  testPreviewLink: () => api.get('/SurveyLinks/test/preview'),
  generateQRCode: (surveyId, linkType) => api.get(`/SurveyLinks/qr/${surveyId}/${linkType}`),
  validateLink: (requestData) => api.post('/SurveyLinks/validate', requestData),
  downloadQRCode: (surveyId) => api.get(`/SurveyLinks/qr/${surveyId}`, { 
    responseType: 'blob',
    timeout: TIMEOUT_CONFIG.download
  }),
};

export const surveyPreviewAPI = {
  getPreview: (surveyId) => api.get(`/SurveyPreview/${surveyId}`),
  getBasicInfo: (surveyId) => api.get(`/SurveyPreview/basic/${surveyId}`),
};

export const sendSurveyAPI = {
  getCompanies: () => api.get('/SendSurvey/companies'),
  getBranches: (companyId) => api.get(`/SendSurvey/branches/${companyId}`),
  getDepartments: (branchId) => api.get(`/SendSurvey/departments/${branchId}`),
  getDivisions: (departmentId) => api.get(`/SendSurvey/divisions/${departmentId}`),
  getEmployees: (params) => api.get('/SendSurvey/employees', { params }),
  getSurveys: () => api.get('/SendSurvey/surveys'),
  sendSurvey: (data) => api.post('/SendSurvey/send', data),
  validateSurveyAccess: (surveyId, token, encodedEmployeeId) => 
    api.get(`/SendSurvey/validate/${surveyId}`, { params: { token, eid: encodedEmployeeId } }),
  getInvitationStatus: (surveyId, employeeId) => 
    api.get(`/SendSurvey/invitation-status/${surveyId}/${employeeId}`),
  resendInvitation: (invitationId) => api.post(`/SendSurvey/resend/${invitationId}`),
  getSendingStats: (surveyId) => api.get(`/SendSurvey/stats/${surveyId}`),
  bulkSend: (data) => api.post('/SendSurvey/bulk-send', data),
  getEmailTemplates: () => api.get('/SendSurvey/email-templates'),
  testEmailConfig: (data) => api.post('/SendSurvey/test-email', data),
  // Reminder endpoints
  getPendingInvitations: (surveyId) => api.get(`/SendSurvey/pending-invitations/${surveyId}`),
  sendReminder: (invitationId) => api.post(`/SendSurvey/send-reminder/${invitationId}`),
  sendRemindersForSurvey: (surveyId) => api.post(`/SendSurvey/send-reminders/${surveyId}`),
  sendAllReminders: () => api.post('/SendSurvey/send-all-reminders'),
  getReminderStats: (surveyId) => api.get(`/SendSurvey/reminder-stats/${surveyId}`),
  getInvitationDetails: (invitationId) => api.get(`/SendSurvey/invitation-details/${invitationId}`),

};
// Users & Permissions API
export const usersPermissionsAPI = {
  getUsers: () => api.get('/UsersPermissions/users'),
  getModules: () => api.get('/UsersPermissions/modules'),
  getUserPermissions: (userId) => api.get(`/UsersPermissions/user-permissions/${userId}`),
  updatePermissions: (permissionsData) => api.post('/UsersPermissions/update-permissions', permissionsData),
  toggleUserStatus: (userId) => api.post(`/UsersPermissions/toggle-user-status/${userId}`),
};

// Also fix other duplicate endpoints:
export const emailSetupAPI = {
  getEmailSetup: () => api.get('/EmailSetup'),
  saveEmailSetup: (setupData) => api.post('/EmailSetup', setupData),
  testEmailSetup: (testData) => api.post('/EmailSetup/test', testData),
  getAllEmailSetups: () => api.get('/EmailSetup/all') // Add this new method
};

export const emailTemplatesAPI = {
  getTemplates: () => api.get('/EmailTemplates'),
  getTemplate: (id) => api.get(`/EmailTemplates/${id}`),
  createTemplate: (template) => api.post('/EmailTemplates', template),
  updateTemplate: (id, template) => api.put(`/EmailTemplates/${id}`, template),
  deleteTemplate: (id) => api.delete(`/EmailTemplates/${id}`),
};
export const transferSurveyAPI = {
  getCompanies: () => api.get('/TransferSurvey/companies'),
  getSurveysByCompany: (companyId) => api.get(`/TransferSurvey/surveys/${companyId}`),
  getSurveyDetails: (surveyId) => api.get(`/TransferSurvey/survey-details/${surveyId}`),
  transferSurvey: (transferData) => api.post('/TransferSurvey/transfer', transferData),
};
export const themesAPI = {
  getThemes: () => api.get('/Themes'),
  getTheme: (id) => api.get(`/Themes/${id}`),
  applyTheme: (applyData) => api.post('/Themes/apply', applyData),
};
export const settingsAPI = {
  getApplicationSettings: () => api.get('/Settings/application'),
  updateApplicationSettings: (settings) => api.post('/Settings/application', settings),
  uploadLogo: (formData) => api.post('/Settings/upload-logo', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  uploadFavicon: (formData) => api.post('/Settings/upload-favicon', formData, {
    headers: { 'Content-Type': 'multipart/form-data' }
  }),
  getThemeOptions: () => api.get('/Settings/themes'),
  getLanguageOptions: () => api.get('/Settings/languages'),
  getTimezoneOptions: () => api.get('/Settings/timezones'),
  resetToDefaults: () => api.post('/Settings/reset-to-defaults'),
  getSystemInfo: () => api.get('/Settings/system-info'),
};
// Survey Responses API
export const surveyResponsesAPI = {
  // ===== PUBLIC SURVEY ENDPOINTS =====
  submitPublicResponse: (submissionData) => api.post('/SurveyResponses/public/submit', submissionData),
  getPublicSurveyStatus: (surveyId, token, encodedEmployeeId) => 
    api.get(`/SurveyResponses/public/status/${surveyId}/${token}/${encodedEmployeeId}`),
  validatePublicSurveyAccess: (surveyId, token, encodedEmployeeId) => 
    api.get(`/SurveyResponses/public/validate/${surveyId}/${token}/${encodedEmployeeId}`),

  // ===== EXISTING DASHBOARD ENDPOINTS =====
  getDashboard: (surveyId) => api.get(`/SurveyResponses/dashboard/${surveyId}`),
  getFilteredResponses: (surveyId, filterData) => api.post(`/SurveyResponses/filter/${surveyId}`, filterData),
  getResponseDetails: (responseId) => api.get(`/SurveyResponses/details/${responseId}`),
  getStatistics: (surveyId) => api.get(`/SurveyResponses/statistics/${surveyId}`),
  exportResponses: (surveyId, format) => api.get(`/SurveyResponses/export/${surveyId}`, { 
    params: { format },
    responseType: 'blob',
    timeout: TIMEOUT_CONFIG.download
  }),
  getTrends: (surveyId, period) => api.get(`/SurveyResponses/trends/${surveyId}`, { 
    params: { period } 
  }),
  getCategoryAnalysis: (surveyId, categoryId) => api.get(`/SurveyResponses/category-analysis/${surveyId}`, {
    params: categoryId ? { categoryId } : {}
  }),
  getEmployeeStatus: (surveyId) => api.get(`/SurveyResponses/employee-status/${surveyId}`),
  sendReminders: (surveyId, reminderData) => api.post(`/SurveyResponses/reminders/${surveyId}`, reminderData),
  getComparativeAnalysis: (surveyIds) => api.post('/SurveyResponses/comparative-analysis', { surveyIds }),
  getWordCloud: (surveyId, questionId) => api.get(`/SurveyResponses/word-cloud/${surveyId}`, {
    params: questionId ? { questionId } : {}
  }),
  getSentimentAnalysis: (surveyId) => api.get(`/SurveyResponses/sentiment-analysis/${surveyId}`),
  deleteResponse: (responseId) => api.delete(`/SurveyResponses/${responseId}`),
  bulkDeleteResponses: (deleteData) => api.post('/SurveyResponses/bulk-delete', deleteData),
  getCompletionRate: (surveyId) => api.get(`/SurveyResponses/completion-rate/${surveyId}`),
  getAverageResponseTime: (surveyId) => api.get(`/SurveyResponses/average-response-time/${surveyId}`),
};
export const questionResponsesAPI = {
  getSurveys: () => api.get('/QuestionResponses/surveys'),
  getEmployeesBySurvey: (surveyId) => api.get(`/QuestionResponses/employees/${surveyId}`),
  getEmployeeDetails: (surveyId, employeeId) => api.get(`/QuestionResponses/details/${surveyId}/${employeeId}`),
  getSummary: (filterData) => api.post('/QuestionResponses/summary', filterData),
  exportResponses: (filterData) => api.post('/QuestionResponses/export', filterData, {
    responseType: 'blob',
    timeout: TIMEOUT_CONFIG.download
  }),
};
export const employeeSurveyReportsAPI = {
  getSurveys: () => api.get('/EmployeeSurveyReports/surveys'),
  getEmployees: (filters) => api.post('/EmployeeSurveyReports/employees', filters),
  generateReport: (request) => api.post('/EmployeeSurveyReports/generate', request),
  // NOTE: this used to be missing responseType: 'blob' -- the backend now returns
  // a real binary file, but without this the browser would try to parse it as JSON
  // and the download would come out corrupted/broken.
  exportReport: (request, format) => api.post('/EmployeeSurveyReports/export', { ...request, format }, {
    responseType: 'blob',
    timeout: TIMEOUT_CONFIG.download
  }),
};
export const reportsAPI = {
  getCompanies: () => api.get('/reports/companies'),
  getFilters: (companyId) => api.get(`/reports/filters/${companyId}`),
  generateReport: (requestData) => api.post('/reports/generate', requestData),
  // New: "All Reports" had no export endpoint or frontend method at all before.
  exportReport: (requestData, format) => api.post('/reports/export', { ...requestData, format }, {
    responseType: 'blob',
    timeout: TIMEOUT_CONFIG.download
  }),
};
export const reportPdfAPI = {
  exportReport: (requestData) => api.post('/report-pdf/export', requestData, {
    responseType: 'blob',
    timeout: TIMEOUT_CONFIG.download
  }),
};
export const averagereportsAPI = {
  getSurveys: () => api.get('/EmployeeAverageReports/surveys'),
  getEmployees: (surveyId) => api.get(`/EmployeeAverageReports/employees/${surveyId}`),
  generateReport: (requestData) => api.post('/EmployeeAverageReports/generate', requestData),
  // New: Average Reports had a dead "Export Report" button with no backing endpoint/method.
  exportReport: (requestData, format) => api.post('/EmployeeAverageReports/export', { ...requestData, format }, {
    responseType: 'blob',
    timeout: TIMEOUT_CONFIG.download
  }),
};
// Add to your existing api.js file
export const dashboardAPI = {
  getStats: () => api.get('/Dashboard/stats'),
  getRecentSurveys: () => api.get('/Dashboard/recent-surveys'),
  getChartData: (chartType, timeframe = '30d') =>
    api.get(`/Dashboard/chart-data/${chartType}?timeframe=${timeframe}`),
  getNpsSummary: () => api.get('/Dashboard/nps-summary'),
  getSatisfactionSummary: () => api.get('/Dashboard/satisfaction-summary'),
  getSentimentSummary: () => api.get('/Dashboard/sentiment-summary'),
  getFeedback: (take = 20) => api.get(`/Dashboard/feedback?take=${take}`),
};
export default api;